import React, { useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import {
  suspendHold,
  SuspendHoldInfo,
} from "@tlc/ls2pac/components/services/Holds";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { useToast } from "@tlc/ui/toast";
import { Dayjs } from "dayjs";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useAnalytics } from "../../../../analytics/useGoogleAnalytics";
import { getKeyForHold, getKeyForSuspendHoldInfo } from "./HoldsSharedView";
import { FormDialog } from "@tlc/ui/dialog/FormDialog";
import { useLocalizationContext } from "@mui/x-date-pickers/internals";
import { ConfirmDialog } from "@tlc/ui/dialog/ConfirmDialog";
import { useErrorDialog } from "@tlc/ui/dialog/error/ErrorDialogContext";

export interface SuspendHoldsDialogProps {
  open: boolean;
  holds: HostSystemItemView[];
  onSuspend: (suspended: SuspendHoldInfo[]) => void;
  onClose: () => void;
}
export interface SuspendHoldsFormInput {
  suspendUntil: Dayjs | null;
}
export const SuspendHoldsDialog: React.FC<SuspendHoldsDialogProps> = ({
  open,
  onSuspend,
  onClose,
  holds,
}) => {
  const { translate } = useLanguage();
  const { handleRequestError } = useErrorDialog();
  const { trackSuspendHold } = useAnalytics();
  const { showSuccessToast, showToast } = useToast();
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
    setFocus,
  } = useForm<SuspendHoldsFormInput>({
    mode: "onTouched",
    defaultValues: {
      suspendUntil: null,
    },
  });

  const [failedHolds, setFailedHolds] = useState<HostSystemItemView[] | null>(
    null,
  );

  const onSubmit: SubmitHandler<SuspendHoldsFormInput> = async (data) => {
    const { suspendUntil } = data;
    if (!suspendUntil) return;
    setFailedHolds(null);

    try {
      const { suspendHoldInfos } = await suspendHold(
        holds,
        suspendUntil.toDate(),
      );
      // TODO track number of days suspended?
      trackSuspendHold(suspendHoldInfos.length);
      const errors = suspendHoldInfos.filter((hold) => hold.success === false);
      const successes = suspendHoldInfos.filter(
        (hold) => hold.success === true,
      );

      if (successes.length > 0) {
        showSuccessToast(
          `${successes.length} ${translate("suspendedMessage")}`,
        );
        onSuspend(successes);
        if (errors.length === 0) {
          onClose();
        }
      }

      if (errors.length > 0) {
        const failedKeys = errors.map(getKeyForSuspendHoldInfo);
        const failedItems = holds.filter((hold) =>
          failedKeys.includes(getKeyForHold(hold)),
        );
        setFailedHolds(failedItems);
      }
    } catch (e) {
      handleRequestError(e);
    }
  };

  const handleExited = () => {
    reset();
  };

  const handleErrorExited = () => {
    reset();
    setFailedHolds(null);
  };

  useEffect(() => {
    if (open) requestAnimationFrame(() => setFocus("suspendUntil"));
  }, [open, setFocus]);

  const adapter = useLocalizationContext();
  const now = adapter.utils.date(undefined);

  if (failedHolds) {
    return (
      <ConfirmDialog
        id="suspend-holds-error-dialog"
        open={open}
        useSingleAction
        onClose={onClose}
        onTransitionExited={handleErrorExited}
        aria-label={translate("suspend")}
        onConfirm={onClose}
        confirmButtonText={translate("ok")}
      >
        <Alert severity="error">
          {failedHolds.length} {translate("suspendHoldFailureMessage")}
        </Alert>
        <List disablePadding>
          {failedHolds.map((hold) => (
            <ListItem key={getKeyForHold(hold)} disableGutters>
              <Typography variant="body2">{hold.title}</Typography>
            </ListItem>
          ))}
        </List>
      </ConfirmDialog>
    );
  }

  return (
    <FormDialog
      open={open}
      maxWidth="xs"
      onClose={onClose}
      onTransitionExited={handleExited}
      confirmButtonText={translate("save")}
      aria-label={translate("suspend")}
      formId="suspend-hold-dialog"
    >
      <form
        id="suspend-hold-dialog"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Box sx={{ mt: 2 }}>
          <Controller
            name="suspendUntil"
            control={control}
            rules={{
              required: translate("requiredFieldError"),
              validate: {
                validDate: (value) =>
                  !value ||
                  adapter.utils.isValid(value) ||
                  translate("invalidDateError"),
                futureDate: (value) =>
                  !value ||
                  adapter.utils.isAfter(value, now) ||
                  translate("disablePastError"),
              },
            }}
            render={({ field: { onChange, value, onBlur, ref } }) => {
              return (
                <DatePicker
                  name="suspendDatePicker"
                  desktopModeMediaQuery="@media (pointer: fine) and (min-width:320px) and (min-height:584px)"
                  inputRef={ref}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.suspendUntil,
                      helperText: errors.suspendUntil?.message || " ",
                      required: true,
                      onBlur,
                    },
                  }}
                  onClose={() => {
                    onBlur();
                  }}
                  onChange={(date) => {
                    console.log(date);
                    if (date) {
                      onChange(date);
                    } else {
                      onChange(null);
                    }
                  }}
                  value={value}
                  disablePast
                  label={translate("suspend")}
                />
              );
            }}
          ></Controller>
        </Box>
      </form>
    </FormDialog>
  );
};
